import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Input } from '@/shared/ui/input';
import { DatePicker } from '@/shared/ui/date-picker';
import { ArrowLeft, Upload, FileText, X, Download } from 'lucide-react';
import { useContract, useCreateContract, useUpdateContract } from '../hooks';
import { isValidFileType, isValidFileSize, sanitizeFileName } from '../utils/contractValidation';
import { useCustomQuery } from '@/shared/hooks/useCustomQuery';
import CreatorService, { Creator } from '@/features/creators/services/creatorService';
import { useToast } from '@/shared/hooks/use-toast';
import { MAX_FILE_SIZE_KB } from '@/shared/lib/fileUploadConstants';
import { format, startOfToday, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { ContractStatus } from '../utils/types';
import { getStatusLabel } from '../utils/contractStatus';
import { useTranslation } from '@/shared/i18n';

const STATUS_OPTIONS = ['DRAFT', 'PENDING_SIGNATURE', 'SIGNED', 'EXPIRED', 'CANCELLED'] as const satisfies readonly ContractStatus[];
type StatusOption = typeof STATUS_OPTIONS[number];

// Schema factory function to use translations
const createFormSchema = (t: (key: string) => string) => z
  .object({
    creatorId: z.string().min(1, t('selectCreator')),
    startDate: z.date({
      required_error: t('startDateRequired'),
    }),
    endDate: z.date({
      required_error: t('endDateRequired'),
    }),
    status: z.enum(STATUS_OPTIONS, {
      required_error: t('selectStatus'),
    }),
    file: z.any().optional(),
  })
  .refine((data) => data.endDate > data.startDate, {
    message: t('endDateAfterStartDate'),
    path: ['endDate'],
  });

type FormData = z.infer<ReturnType<typeof createFormSchema>>;

export const ContractForm = () => {
  const { t } = useTranslation('contracts');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;
  const { toast } = useToast();
  const today = useMemo(() => startOfToday(), []);
  
  const formSchema = createFormSchema(t);

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const {
    data: creators = [],
    isLoading: isLoadingCreators,
    error: creatorsError,
  } = useCustomQuery<
    typeof CreatorService.Instance,
    undefined,
    Creator[]
  >({
    serviceInstance: CreatorService.Instance,
    selectAction: (service) => service.getCreators,
    parameter: undefined,
    options: {
      staleTime: 10 * 60 * 1000,
    },
  });

  // Hooks para operações CRUD
  const { contract, isLoading: isLoadingContract } = useContract(id || '');
  const { createContract, isLoading: isCreating } = useCreateContract();
  const { updateContract, isLoading: isUpdating } = useUpdateContract();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      creatorId: '',
      startDate: undefined,
      endDate: undefined,
      status: 'PENDING_SIGNATURE',
    },
  });

  const isSubmitting = isCreating || isUpdating;
  const sortedContractFiles = useMemo(() => {
    if (!contract?.files) return [];
    return [...contract.files].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [contract]);

  const startDateValue = form.watch('startDate');

  const minEndDate = useMemo(() => {
    if (startDateValue instanceof Date) {
      const startDay = startOfDay(startDateValue);
      return startDay ?? today
    }
    return today;
  }, [startDateValue, today]);

  const formatFileSize = (size: number) => {
    if (!size) return '0 KB';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = size;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }

    const formattedValue =
      value < 10 && unitIndex > 0 ? value.toFixed(1) : Math.round(value).toString();

    return `${formattedValue} ${units[unitIndex]}`;
  };

  const formatFileDate = (isoDate: string) => {
    try {
      return format(new Date(isoDate), 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch {
      return '-';
    }
  };

  // Carrega dados do contrato ao editar
  useEffect(() => {
    if (!isEditing || !contract) {
      return;
    }

    const normalizedStatus =
      STATUS_OPTIONS.find((status) => status === contract.status) ?? 'PENDING_SIGNATURE';

    form.reset({
      creatorId: contract.creatorId ?? '',
      startDate: contract.startDate ? new Date(contract.startDate) : undefined,
      endDate: contract.endDate ? new Date(contract.endDate) : undefined,
      status: normalizedStatus,
      file: undefined,
    });
    setUploadedFile(null);
  }, [
    isEditing,
    contract?.id,
    contract?.creatorId,
    contract?.startDate,
    contract?.endDate,
    contract?.status,
    form,
  ]);

  useEffect(() => {
    if (creatorsError) {
      const message =
        creatorsError instanceof Error
          ? creatorsError.message
          : t('couldNotLoadCreators');

      toast({
        title: t('errorLoadingCreators'),
        description: message,
        variant: 'destructive',
      });
    }
  }, [creatorsError, toast, t]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validação de tipo
    if (!isValidFileType(file)) {
      toast({
        title: t('invalidFile'),
        description: t('onlyPdfAllowed'),
        variant: 'destructive',
      });
      return;
    }

    // Validação de tamanho (500KB máximo)
    if (!isValidFileSize(file)) {
      toast({
        title: t('fileTooLarge'),
        description: t('maxFileSize'),
        variant: 'destructive',
      });
      return;
    }

    const sanitizedFileName = sanitizeFileName(file.name);
    const sanitizedFile =
      sanitizedFileName === file.name
        ? file
        : new File([file], sanitizedFileName, { type: file.type });

    setUploadedFile(sanitizedFile);
    form.setValue('file', sanitizedFile);
  };

  const removeFile = () => {
    setUploadedFile(null);
    form.setValue('file', undefined);
  };

  const onSubmit = async (data: FormData) => {
    // Validação de arquivo (obrigatório ao criar)
    if (!isEditing && !uploadedFile) {
      toast({
        title: t('fileRequired'),
        description: t('uploadPdfRequired'),
        variant: 'destructive',
      });
      return;
    }

    try {
      const payload = {
        creatorId: data.creatorId,
        startDate: data.startDate.toISOString(),
        endDate: data.endDate.toISOString(),
        status: data.status,
        file: uploadedFile || undefined,
      };

      if (isEditing && id) {
        await updateContract(id, payload);
      } else {
        await createContract(payload);
      }

      navigate('/contracts');
    } catch (error) {
      // Erro já tratado nos hooks
      console.error('Erro ao salvar contrato:', error);
    }
  };

  // Loading state para edição
  if (isEditing && isLoadingContract) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/contracts')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t('loading')}</h1>
            <p className="text-muted-foreground">{t('loadingContract')}</p>
          </div>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/contracts')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEditing ? t('editContract') : t('newContract')}
          </h1>
          <p className="text-muted-foreground">
            {isEditing
              ? t('updateContractInfo')
              : t('fillContractData')}
          </p>
        </div>
      </div>

      {/* Formulário */}
      <Card className='bg-white/[0.03] border-white/[0.06]'>
        <CardHeader>
          <CardTitle>{t('contractInfo')}</CardTitle>
          <CardDescription>
            {t('allFieldsRequired')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isEditing && sortedContractFiles.length > 0 && (
            <div className="mb-6 space-y-3">
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('contractFiles')}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {t('lastFilesFirst')}
                </p>
              </div>
              <div className="space-y-2">
                {sortedContractFiles.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 p-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium break-all">
                        {file.fileName}
                        {file.isActive && (
                          <Badge className="ml-2" variant="secondary">
                            {t('active')}
                          </Badge>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(file.fileSize)} • {t('uploadedOn')} {formatFileDate(file.createdAt)}
                      </p>
                    </div>
                    <Button
                      asChild
                      variant="ghost"
                      size="icon"
                      title={t('downloadFile', { fileName: file.fileName })}
                      className="shrink-0"
                    >
                      <a
                        href={file.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t('downloadFile', { fileName: file.fileName })}
                      >
                        <Download className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Creator */}
              <div className="grid gap-6 md:grid-cols-2">

                <FormField
                  control={form.control}
                  name="creatorId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('creator')}</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={isLoadingCreators || !!creatorsError}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t('selectCreator')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {isLoadingCreators && (
                            <SelectItem value="__loading" disabled>
                              {t('loadingCreators')}
                            </SelectItem>
                          )}
                          {!isLoadingCreators && creators.length === 0 && (
                            <SelectItem value="__empty" disabled>
                              {t('noCreatorsFound')}
                            </SelectItem>
                          )}
                          {creators.map((creator) => (
                            <SelectItem key={creator.id} value={creator.id}>
                              <div className="flex flex-col">
                                <span>{creator.name}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        {t('selectCreatorDescription')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* Status */}
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('status')}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t('selectStatus')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {STATUS_OPTIONS.map((status) => (
                            <SelectItem key={status} value={status}>
                              {getStatusLabel(status)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        {t('defineStatusDescription')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {/* Datas */}
              <div className="grid gap-6 md:grid-cols-2">
                {/* Data de Início */}
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('startDate')}</FormLabel>
                      <DatePicker
                        date={field.value}
                        onSelect={field.onChange}
                        placeholder={t('selectStartDate')}
                      />
                      <FormDescription>{t('startDateDescription')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Data de Fim */}
                <FormField
                  control={form.control}
                  name="endDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('endDate')}</FormLabel>
                      <DatePicker
                        date={field.value}
                        onSelect={field.onChange}
                        placeholder={t('selectEndDate')}
                        minDate={minEndDate}
                      />
                      <FormDescription>{t('endDateDescription')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />


              </div>

              {/* Upload de Arquivo */}
              <FormField
                control={form.control}
                name="file"
                render={() => (
                  <FormItem>
                    <FormLabel>{t('contractFile')} {!isEditing && '*'}</FormLabel>
                    <FormControl>
                      <div className="space-y-4">
                        {/* Arquivo selecionado */}
                        {uploadedFile && (
                          <div className="flex items-center gap-2 rounded-md border border-secondary bg-secondary/5 p-4">
                            <FileText className="h-5 w-5 text-primary" />
                            <div className="flex-1">
                              <p className="text-sm font-medium">{uploadedFile.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={removeFile}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        )}

                        {/* Botão de upload */}
                        <label htmlFor="file-upload">
                          <div className="flex cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-dashed p-6 transition-colors hover:border-neutral-600 hover:bg-neutral-600/5">
                            <Upload className="h-5 w-5 text-muted-foreground" />
                            <div className="text-center">
                              <p className="text-sm font-medium">
                                {isEditing ? t('addNewFile') : t('clickToUpload')}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {t('onlyPdfMax', { size: MAX_FILE_SIZE_KB })}
                              </p>
                            </div>
                          </div>
                          <Input
                            id="file-upload"
                            type="file"
                            accept=".pdf,application/pdf"
                            onChange={handleFileChange}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </FormControl>
                    <FormDescription>
                      {isEditing
                        ? t('sendNewPdf')
                        : t('uploadContractPdf')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Botões de ação */}
              <div className="flex justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/contracts')}
                  disabled={isSubmitting}
                >
                  {t('cancel')}
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting 
                    ? (isEditing ? t('updating') : t('creating'))
                    : (isEditing ? t('update') : t('create'))
                  }
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};
